import { fromJS, type List, Map } from '@gonfalon/immutable-shim/immutableShim';
import nullthrows from 'nullthrows';
import { createSelector } from 'reselect';

import type { EnvironmentAction } from '#actions/environments';
import type { ProjectAction } from '#actions/projects';
import { useSelector } from '#hooks/useSelector';
import { createRequestReducerByKey } from '#reducers/createRequestReducer';
import { paginate } from '#reducers/paginate';
import type { GlobalState } from '#reducers/state';
import type { ParamsProps } from '#reducers/types/shared';
import { getProjectForEnvById } from '#utils/environments/projectLink';
import type { Environment } from '#utils/environments/records/Environment';
import type { Flag } from '#utils/flags/records/Flag';
import { createProjectFiltersFromQuery } from '#utils/projects/records/Filters';
import type { Project } from '#utils/projects/records/Project';
import { createProject } from '#utils/projects/records/Project';
import type { Request, StateWithRequestInfo } from '#utils/requestUtils';

import { getProjectAndEnvironmentFromUrl } from '../getProjectAndEnvironmentFromUrl';

import { type CurrentEnvironmentState, getCurrentEnvironmentInitialState } from './getCurrentEnvironmentInitialState';
import {
  type EnvironmentEntities,
  type EnvironmentsState,
  getEnvironmentsInitialState,
} from './getEnvironmentsInitialState';
import { getProjectsInitialState, type ProjectEntities, type ProjectsState } from './getProjectsInitialState';
import { reducerRegistry as registry } from './registry';
import { searchSelector } from './router';

type ProjectRequest = StateWithRequestInfo<{
  entity: Project;
}>;

export function projects(
  state: ProjectsState = getProjectsInitialState(),
  action: ProjectAction | EnvironmentAction,
): ProjectsState {
  switch (action.type) {
    case 'environments/REQUEST_CURRENT_PROJECT_AND_ENVIRONMENT_DONE':
      return state.withMutations((st) =>
        st.merge({
          isFetching: false,
          lastFetched: action.timestamp,
          entities: Map({
            [action.project.key]: action.project,
          }),
        }),
      );
    case 'projects/REQUEST_PROJECT_DONE':
      return state.withMutations((st) => {
        const project = action.response.get('result');
        st.update('entities', (entities: ProjectEntities) => entities.set(project.key, project));
      });
    case 'projects/DELETE_PROJECT_DONE':
      return state.update('entities', (entities) => entities.delete(action.project.key));
    case 'environments/CREATE_ENVIRONMENT_DONE':
      return state.updateIn(['entities', action.project.key, 'environments'], (envs) => envs.push(action.env._id));
    case 'environments/DELETE_ENVIRONMENT_DONE':
      return state.updateIn(['entities', action.project.key, 'environments'], (envs) =>
        envs.delete(envs.indexOf(action.env._id)),
      );

    case 'projects/DELETE_PROJECT_FAILED':
    case 'environments/DELETE_ENVIRONMENT_FAILED':
    case 'environments/UPDATE_ENVIRONMENT_FAILED':
    case 'environments/RESET_API_KEY_FAILED':
    case 'environments/RESET_MOBILE_KEY_FAILED':
    case 'environments/CREATE_ENVIRONMENT_FAILED':
      if (action.error.get('status') !== 404) {
        return state;
      }
      return state.delete('lastFetched');
    default:
      return state;
  }
}

export const paginatedProjectEnvironments = paginate({
  types: [
    'projects/REQUEST_PROJECT_ENVIRONMENTS',
    'projects/REQUEST_PROJECT_ENVIRONMENTS_FAILED',
    'projects/REQUEST_PROJECT_ENVIRONMENTS_DONE',
  ],
  invalidateTypes: [
    'projects/REQUEST_PROJECT_ENVIRONMENTS_DONE',
    'projects/REQUEST_PROJECT_ENVIRONMENTS_FAILED',
    'environments/CREATE_ENVIRONMENT_DONE',
    'environments/DELETE_ENVIRONMENT_DONE',
  ],
  mapActionToKey: () => 'all',
});

export function projectEnvironments(
  state: EnvironmentsState = getEnvironmentsInitialState(),
  action: ProjectAction | EnvironmentAction,
) {
  switch (action.type) {
    case 'environments/UPDATE_ENVIRONMENT_DONE':
      const envEntities = state.get('entities');
      const sortByCritical = (a?: Environment, b?: Environment) =>
        a && b && a.get('critical') && !b.critical ? -1 : 1;
      return state.merge({
        entities: envEntities.set(action.env._id, action.env).sort((a, b) => sortByCritical(a, b)),
        isFetching: false,
        lastFetched: Date.now(),
      });
    case 'projects/REQUEST_PROJECT_DONE':
      return state.merge({ lastFetched: action.timestamp, isFetching: false });
    case 'projects/REQUEST_PROJECT_ENVIRONMENTS':
    case 'environments/UPDATE_ENVIRONMENT_FAILED':
      return state.merge({
        isFetching: false,
        lastFetched: Date.now(),
      });
    case 'projects/REQUEST_PROJECT_ENVIRONMENTS_DONE':
      return state.merge({
        isFetching: false,
        lastFetched: Date.now(),
        entities: action.response.getIn(['entities', 'environments']),
      });
    case 'projects/REQUEST_PROJECT_ENVIRONMENTS_FAILED':
      return state.merge({
        isFetching: false,
      });
    case 'environments/DELETE_ENVIRONMENT_DONE':
      return state.update('entities', (entities: EnvironmentEntities) => entities.delete(action.env._id));
    default:
      return state;
  }
}

export function environments(
  state: EnvironmentsState = getEnvironmentsInitialState(),
  action: ProjectAction | EnvironmentAction,
) {
  switch (action.type) {
    case 'projects/REQUEST_PROJECT_DONE':
      return state.update('entities', (entities: EnvironmentEntities) =>
        entities.merge(action.response.getIn(['entities', 'environments'])),
      );
    case 'environments/CREATE_ENVIRONMENT_DONE':
      return state.update('entities', (entities: EnvironmentEntities) => entities.set(action.env._id, action.env));
    case 'environments/UPDATE_ENVIRONMENT_DONE':
      return state.update('entities', (entities: EnvironmentEntities) => entities.set(action.env._id, action.env));
    case 'environments/RESET_API_KEY_DONE':
    case 'environments/RESET_MOBILE_KEY_DONE':
      return state.update('entities', (entities: EnvironmentEntities) => entities.set(action.env._id, action.env));
    case 'environments/DELETE_ENVIRONMENT_DONE':
      return state.update('entities', (entities: EnvironmentEntities) => entities.delete(action.env._id));
    case 'environments/FOLLOW_ENVIRONMENT':
      return state;
    // revert the environment if updating the preference failed
    case 'environments/FOLLOW_ENVIRONMENT_FAILED':
      return state;
    default:
      return state;
  }
}

export function currentEnvironment(
  state = getCurrentEnvironmentInitialState(),
  action: EnvironmentAction,
): CurrentEnvironmentState {
  switch (action.type) {
    case 'environments/REQUEST_CURRENT_PROJECT_AND_ENVIRONMENT':
      return state.set('isFetching', true);
    case 'environments/REQUEST_CURRENT_PROJECT_AND_ENVIRONMENT_FAILED':
      return state.merge({
        isFetching: false,
        error: action.error,
        doNotFetch: true,
      });
    case 'environments/REQUEST_CURRENT_PROJECT_AND_ENVIRONMENT_DONE':
      return state.withMutations((st: CurrentEnvironmentState) =>
        st.merge({
          isFetching: false,
          lastFetched: action.timestamp,
          entity: action.environment._id,
        }),
      );
    default:
      return state;
  }
}

export const environmentTextFilters = (state: Map<string, string> = Map(), action: EnvironmentAction) => {
  switch (action.type) {
    case 'environments/FILTER_ENVIRONMENTS_BY_TEXT':
      return state.set(action.project.key, action.term);
    default:
      return state;
  }
};

export const projectRequestByKey = createRequestReducerByKey(
  ['projects/REQUEST_PROJECT', 'projects/REQUEST_PROJECT_DONE', 'projects/REQUEST_PROJECT_FAILED'],
  (action) => action.projectKey,
);

const projectsSelector = (state: GlobalState): ProjectsState => state.projects;

export const projectEntitiesSelector = (state: GlobalState) => projectsSelector(state).get('entities');

export const projectByKeySelector = createSelector<
  GlobalState,
  ParamsProps<{ projKey: string }>,
  ProjectsState,
  string,
  ProjectRequest
>(
  projectsSelector,
  (state, props) => (props.match ? props.match.params.projKey : nullthrows(props.params).projKey),
  (projectsState, projKey) =>
    fromJS({
      lastFetched: projectsState.get('lastFetched'),
      isFetching: projectsState.get('isFetching'),
      entity: projectsState.getIn(['entities', projKey]),
    }),
);

export const makeProjectSelectorForEnvId = (envId: string) => (state: GlobalState) => {
  const projectsState = projectsSelector(state);
  return fromJS({
    lastFetched: projectsState.get('lastFetched'),
    isFetching: projectsState.get('isFetching'),
    entity: getProjectForEnvById(envId, projectsState.get('entities')),
  });
};

export const environmentsSelector = (state: GlobalState) => state.environments;

export const environmentEntitiesSelector = (state: GlobalState) => environmentsSelector(state).get('entities');

export const projectEnvironmentsSelector = (state: GlobalState) => state.projectEnvironments;

export const makeProjectEnvironmentsSelector = () =>
  createSelector<GlobalState, { project: Project }, EnvironmentEntities, Project, List<Environment>>(
    environmentEntitiesSelector,
    (_, props) => props.project,
    (entities, project) => project.environments.map((id) => entities.get(id)).filter((env) => !!env),
  );

const currentEnvironmentIdSelector = (state: GlobalState) => state.currentEnvironment;

export const currentEnvironmentSelector = createSelector(
  environmentsSelector,
  currentEnvironmentIdSelector,
  (environmentsState, id) =>
    fromJS({
      lastFetched: id.get('lastFetched'),
      doNotFetch: id.get('doNotFetch'),
      isFetching: id.get('isFetching'),
      entity: environmentsState.getIn(['entities', id.get('entity')]),
    }),
);

export function useCurrentEnvironmentEntity(): Environment {
  return useSelector(currentEnvironmentSelector)?.get('entity');
}

export function useCurrentEnvironmentKey() {
  return useCurrentEnvironmentEntity()?.get('key');
}

export function useCurrentProjectEnvironments() {
  const project = useCurrentProjectEntity();
  return useSelector((state) => makeProjectEnvironmentsSelector()(state, { project }));
}

export function useIsApprovalRequiredForFlagInCurrentEnvironment(flag?: Flag) {
  const { approvalSettings } = useCurrentEnvironmentEntity();
  return !!flag?.isApprovalRequired(approvalSettings);
}

export function useBypassRequiredApprovalsInCurrentEnvironment(flag?: Flag) {
  const { key } = useCurrentEnvironmentEntity();
  return flag?.canBypassRequiredApprovals(key);
}

const findCurrentProjectByKey = (projectsEntities: ProjectEntities, projectKey: string | undefined) =>
  projectKey ? projectsEntities.get(projectKey) : undefined;

type CurrentProject = StateWithRequestInfo<{
  entity: Project;
  doNotFetch: boolean;
}>;

export const currentProjectSelector = createSelector(projectsSelector, (projectsState) => {
  const url = new URL(window.location.pathname, window.location.origin);
  const { project } = getProjectAndEnvironmentFromUrl(url);

  return fromJS({
    lastFetched: projectsState.get('lastFetched'),
    doNotFetch: projectsState.get('doNotFetch'),
    error: projectsState.get('error'),
    isFetching: projectsState.get('isFetching'),
    entity: findCurrentProjectByKey(projectsState.get('entities'), project ?? '') || createProject(),
  }) as CurrentProject;
});

export const currentProjectKeySelector = createSelector(
  currentProjectSelector,
  (project) => project.getIn(['entity', 'key']) as string,
);

export const currentProjectEntitySelector = createSelector(currentProjectSelector, (project) =>
  project.getIn(['entity']),
);

export const currentProjectNameSelector = createSelector(currentProjectSelector, (project) =>
  project.getIn(['entity', 'name']),
);

export function useCurrentProjectEntity(): Project {
  return useSelector(currentProjectEntitySelector);
}

export function useCurrentProjectKey() {
  return useSelector(currentProjectKeySelector);
}

export const currentEnvironmentKeySelector = createSelector(currentEnvironmentSelector, (env): string | undefined =>
  env.getIn(['entity', 'key']),
);

export const currentEnvironmentEntitySelector = createSelector(currentEnvironmentSelector, (environment) =>
  environment.getIn(['entity']),
);

export function getCheckAccessResourceEnvironment(state = false, action: EnvironmentAction) {
  switch (action.type) {
    case 'environments/CHECK_ACCESS_RESOURCE':
      return action.willRemoveEditingAbility;
    case 'environments/UPDATE_ENVIRONMENT_DONE':
      return false;
    default:
      return state;
  }
}

export const projectFilterSelector = createSelector(searchSelector, (query) => createProjectFiltersFromQuery(query));

export const projectRequestByKeySelector = createSelector<
  GlobalState,
  { projectKey: string },
  Map<string, Request>,
  string,
  Request | undefined
>(
  (state) => state.projectRequestByKey,
  (_, props) => props.projectKey,
  (requests, projectKey) => requests.get(projectKey),
);

registry.addReducers({
  currentEnvironment,
  environments,
  projectEnvironments,
  paginatedProjectEnvironments,
  environmentTextFilters,
  projects,
  projectRequestByKey,
});

declare module '#reducers/state' {
  interface GlobalState {
    projects: ReturnType<typeof projects>;
    projectEnvironments: ReturnType<typeof projectEnvironments>;
    environments: ReturnType<typeof environments>;
    currentEnvironment: ReturnType<typeof currentEnvironment>;
    environmentTextFilters: ReturnType<typeof environmentTextFilters>;
    projectRequestByKey: ReturnType<typeof projectRequestByKey>;
    paginatedProjectEnvironments: ReturnType<typeof paginatedProjectEnvironments>;
  }
}
